import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, eur, formatError } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { ArrowLeft, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";

export function ShopList() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("tutte");
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  const cats = useMemo(() => ["tutte", ...Array.from(new Set(products.map((p) => p.category)))], [products]);
  const visible = cat === "tutte" ? products : products.filter((p) => p.category === cat);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="shop-page">
      <div className="max-w-3xl">
        <div className="overline text-[#B97A48] mb-4">Kit Fai Da Te</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-[1.05]">Manutenzione<br /><span className="italic">a casa tua.</span></h1>
        <p className="mt-6 text-[#5C5A56] text-lg leading-relaxed max-w-2xl">
          Il kit premium pensato dal nostro laboratorio per la cura quotidiana delle tue calzature.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 border-b border-[#E4DFD5] pb-4">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm transition-colors ${cat === c ? "bg-[#1C1B1A] text-white" : "text-[#5C5A56] hover:text-[#1C1B1A]"}`} data-testid={`filter-${c}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {visible.map((p, idx) => (
          <Link to={`/shop/${p.slug}`} key={p.id} className="fade-up" style={{ animationDelay: `${idx * 0.04}s` }} data-testid={`shop-item-${p.slug}`}>
            <div className="aspect-square overflow-hidden bg-[#F4F1EA] border border-[#E4DFD5]">
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
            <div className="pt-4">
              <div className="overline text-[#B97A48] text-[0.65rem]">{p.category}</div>
              <div className="mt-1 flex items-baseline justify-between gap-2">
                <h3 className="font-medium text-[#1C1B1A] flex-1">{p.name}</h3>
                <span className="font-serif text-xl">{eur(p.price)}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch((e) => setErr(formatError(e)));
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-20 text-[#9E413B]">{err}</div>;
  if (!p) return <div className="max-w-7xl mx-auto px-6 py-20">Caricamento…</div>;

  const onAdd = () => {
    add(p, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="product-detail">
      <Link to="/shop" className="text-sm text-[#5C5A56] hover:text-[#1C1B1A] inline-flex items-center gap-1 mb-8"><ArrowLeft size={14} /> Tutti i prodotti</Link>
      <div className="grid lg:grid-cols-2 gap-16">
        <div className="aspect-square bg-[#F4F1EA] border border-[#E4DFD5] overflow-hidden">
          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
        </div>
        <div>
          <div className="overline text-[#B97A48] mb-2">{p.category}</div>
          <h1 className="font-serif text-5xl leading-tight">{p.name}</h1>
          <div className="mt-4 font-serif text-4xl text-[#B97A48]">{eur(p.price)}</div>
          <p className="mt-6 text-[#5C5A56] leading-relaxed">{p.description}</p>

          <div className="mt-10 flex items-center gap-4">
            <div className="inline-flex items-center border border-[#E4DFD5] rounded-sm">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#F4F1EA]" data-testid="qty-minus"><Minus size={14} /></button>
              <span className="px-4 min-w-[2.5rem] text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#F4F1EA]" data-testid="qty-plus"><Plus size={14} /></button>
            </div>
            <button onClick={onAdd} className="btn-primary" data-testid="add-to-cart-btn">
              <ShoppingBag size={16} strokeWidth={1.5} />
              {added ? "Aggiunto!" : "Aggiungi al carrello"}
            </button>
          </div>
          <div className="mt-10 pt-8 border-t border-[#E4DFD5] text-xs text-[#5C5A56] space-y-2">
            <div>· Spedizione gratuita in Italia oltre 50€</div>
            <div>· Reso entro 14 giorni</div>
            <div>· Pagamento sicuro</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CartPage() {
  const { items, remove, setQty, subtotal, count } = useCart();
  const nav = useNavigate();

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="cart-page">
      <div className="overline text-[#B97A48] mb-3">Il tuo carrello</div>
      <h1 className="font-serif text-5xl leading-tight">Carrello</h1>

      {count === 0 ? (
        <div className="mt-16 text-center py-20 border border-dashed border-[#E4DFD5]" data-testid="empty-cart">
          <ShoppingBag size={40} strokeWidth={1} className="mx-auto text-[#5C5A56]" />
          <p className="mt-4 text-[#5C5A56]">Il tuo carrello è vuoto.</p>
          <Link to="/shop" className="btn-primary mt-6">Vai alla boutique</Link>
        </div>
      ) : (
        <div className="mt-12 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 divide-y divide-[#E4DFD5]">
            {items.map((i) => (
              <div key={i.id} className="py-6 flex gap-4 items-center" data-testid={`cart-item-${i.slug}`}>
                <div className="w-20 h-20 bg-[#F4F1EA] overflow-hidden flex-shrink-0">
                  <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{i.name}</div>
                  <div className="text-xs text-[#5C5A56] mt-1">{eur(i.price)} cad.</div>
                </div>
                <div className="inline-flex items-center border border-[#E4DFD5] rounded-sm">
                  <button onClick={() => setQty(i.id, i.quantity - 1)} className="p-2 hover:bg-[#F4F1EA]"><Minus size={12} /></button>
                  <span className="px-3 text-sm">{i.quantity}</span>
                  <button onClick={() => setQty(i.id, i.quantity + 1)} className="p-2 hover:bg-[#F4F1EA]"><Plus size={12} /></button>
                </div>
                <div className="w-20 text-right font-serif text-lg">{eur(i.price * i.quantity)}</div>
                <button onClick={() => remove(i.id)} className="text-[#5C5A56] hover:text-[#9E413B]" data-testid={`remove-${i.slug}`}><Trash2 size={16} strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
          <div>
            <div className="card-luxury p-6 sticky top-24">
              <div className="overline mb-4">Riepilogo</div>
              <div className="flex justify-between text-sm py-2"><span>Subtotale</span><span>{eur(subtotal)}</span></div>
              <div className="flex justify-between text-sm py-2"><span>Spedizione</span><span className="text-[#5C5A56]">{subtotal >= 50 ? "Gratis" : eur(6.9)}</span></div>
              <div className="flex justify-between text-lg font-serif pt-4 mt-4 border-t border-[#E4DFD5]">
                <span>Totale</span>
                <span className="text-[#B97A48]" data-testid="cart-total">{eur(subtotal + (subtotal >= 50 ? 0 : 6.9))}</span>
              </div>
              <button onClick={() => nav("/checkout")} className="btn-primary w-full justify-center mt-6" data-testid="checkout-btn">
                Procedi al checkout
              </button>
              <div className="text-xs text-[#5C5A56] mt-3 text-center">Pagamento sicuro · Spedizione SDA o DHL</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
